import { DOWNLOAD_APPLICATION_DOCUMENTS_ZIP, GET_APPLICATION_DOCUMENTS, get_headers, REQUEST_INFO_URL, UPLOAD_ADDITIONAL_DOCUMENTS, VERIFY_DOCUMENT_URL } from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import formatting_mixins from '@/utils/mixins/formatting_mixins';
import axios from 'axios';
import { build_document_request_params, build_document_table_rows, save_zip_blob } from './document_management_helpers.js';

export default {
  name: 'DocumentManagement',
  mixins: [alert_mixin, formatting_mixins],
  emits: ['files-saved'],
  props: { application_form_id: { type: [String, Number], default: null }, entity_profile_id: { type: [String, Number], default: null } },

  data: () => ({
    headers: get_headers(),
    application_id: null,
    documents: [],
    selected_document: null,
    table_safe_documents: [],
    answered_file_questions: [],
    request_info_note: '',
    selected_request_fields: [],
    attachments: [{ document_name: '', file: null }],
    is_uploading_documents: false,
    is_downloading_zip: false,
  }),

  mounted() {
    this.application_id = sessionStorage.getItem('application_id');
    if (this.application_id) {
      this.get_documents();
    }
  },

  watch: { profile_role_form_id(new_value) { if (new_value) this.get_documents(); } },

  computed: {
    has_unverified_documents() { return this.table_safe_documents.some((doc) => !doc.is_verified); },
  },

  methods: {
    select_document(doc) {
      this.selected_document = doc;
    },

    async get_documents() {
      this.documents = [];
      await this.load_current_documents();
    },

    async load_current_documents() {
      try {
        const response = await axios.get(GET_APPLICATION_DOCUMENTS, {
          headers: this.headers,
          params: this.build_document_request_params(),
        });
        this.documents = response.data?.data || [];
        this.transform_documents_data();
      } catch (error) {
        this.show_error(this.build_error_message(error, 'Error loading documents'));
      }
    },

    build_error_message(error, prefix) {
      const message = error.response?.data?.message || error.message;
      return `${prefix}: ${message}`;
    },

    transform_documents_data() {
      this.table_safe_documents = build_document_table_rows(
        this.documents,
        this.format_date,
      );
      this.selected_document = this.table_safe_documents[0] || null;
    },

    add_attachment() {
      this.attachments.push({ document_name: '', file: null });
    },

    remove_attachment(index) {
      this.attachments.splice(index, 1);
    },

    handle_file_upload(event, index) {
      const file = event.target.files[0];
      if (file && file.size <= 5 * 1024 * 1024) {
        this.attachments[parseInt(index, 10)].file = file;
        return;
      }
      this.show_error('Invalid file type or size exceeds 5MB.');
    },

    async upload_additional_documents() {
      this.is_uploading_documents = true;
      const form_data = new FormData();
      form_data.append('application_id', this.application_id);
      this.attachments.forEach((attachment) => {
        if (attachment.file) {
          form_data.append('files', attachment.file);
          form_data.append('document_names', attachment.document_name);
        }
      });
      try {
        const response = await axios.post(UPLOAD_ADDITIONAL_DOCUMENTS, form_data, {
          headers: { ...this.headers, 'Content-Type': 'multipart/form-data' },
        });
        this.show_success(response.data.message || 'Documents uploaded successfully!');
        this.$emit('files-saved');
        this.close_modal('upload_additional_documents_modal');
        await this.get_documents();
      } catch (error) {
        this.show_error(
          error.response?.data?.message || 'Failed to upload additional documents.',
        );
      } finally {
        this.is_uploading_documents = false;
      }
    },

    download_file(url, file_name) {
      if (!url) {
        this.show_error('Download URL is missing.');
        return;
      }
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file_name || 'document.pdf');
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async download_documents_zip() {
      if (!this.application_id) {
        this.show_error('Application context is missing.');
        return;
      }
      this.is_downloading_zip = true;
      try {
        const response = await axios.get(DOWNLOAD_APPLICATION_DOCUMENTS_ZIP, {
          headers: this.headers,
          params: this.build_document_request_params(),
          responseType: 'blob',
        });
        save_zip_blob(response);
      } catch (error) {
        this.show_error(
          error.response?.data?.message || 'Failed to download documents.',
        );
      } finally {
        this.is_downloading_zip = false;
      }
    },

    build_document_request_params() {
      return build_document_request_params(this.application_id, this.entity_profile_id);
    },

    open_request_modal() {
      this.request_info_note = '';
      this.selected_request_fields = [];
      this.answered_file_questions = this.table_safe_documents.filter(
        (doc) => !doc.is_verified && doc.file_name && doc.question_id && doc.section,
      );
    },

    toggle_requested_field(event, question) {
      const field = {
        form_section_id: question.section_id,
        form_section_name: question.section,
        question_id: question.question_id,
        requested_field_id: question.question_id,
        requested_field_text: question.question_text,
      };
      const exists = this.selected_request_fields.some(
        (item) => item.requested_field_id === field.requested_field_id,
      );
      if (event.target.checked && !exists) {
        this.selected_request_fields.push(field);
      } else if (!event.target.checked && exists) {
        this.selected_request_fields = this.selected_request_fields.filter(
          (item) => item.requested_field_id !== field.requested_field_id,
        );
      }
    },

    validate_request_information_inputs() {
      if (!this.request_info_note.trim()) {
        this.show_error('Please enter a note.');
        return false;
      }
      if (this.selected_request_fields.length === 0) {
        this.show_error('Please select at least one field.');
        return false;
      }
      return true;
    },

    async request_information() {
      if (!this.validate_request_information_inputs()) {
        return;
      }
      const data = {
        application_form_id: this.application_form_id,
        message: this.request_info_note,
        requested_fields: this.selected_request_fields,
      };
      try {
        const response = await axios.post(REQUEST_INFO_URL, data, {
          headers: this.headers,
        });
        if (response.data.status !== 'success') {
          this.show_error('Error during the form submit');
          return;
        }
        this.close_modal('request_information');
        this.show_success('Information successfully requested');
      } catch (error) {
        if (error.response?.data?.message) {
          this.show_error(error.response.data.message);
        }
      }
    },

    async verify_document(row) {
      const payload = {
        is_verified: true,
        application_form_id: this.application_form_id,
        section_id: row.section_id,
        question_id: row.section_question_id,
        verification_comment: '',
      };
      try {
        await axios.post(VERIFY_DOCUMENT_URL, payload, { headers: this.headers });
        this.show_success('Document verified successfully');
        await this.get_documents();
      } catch (error) {
        this.show_error(
          this.build_error_message(error, 'Verification failed'),
        );
      }
    },
  },
};
